import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NAV_ITEMS, NavItem, isActivePath } from './Sidebar';

const Slot: React.FC<{ item: NavItem; active: boolean; onClick: () => void }> = ({ item, active, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      className="flex h-full flex-1 cursor-pointer items-center justify-center border-none bg-transparent p-0 active:opacity-55"
    >
      <span
        className={`flex h-10 w-[50px] items-center justify-center rounded-full transition-colors duration-200 ${
          active ? 'bg-surface-highlight text-accent' : 'text-on-surface-variant'
        }`}
      >
        <Icon size={22} strokeWidth={active ? 2.25 : 2} />
      </span>
    </button>
  );
};

/**
 * Phone-width navigation — mobile's floating pill tab bar, on the web: icon-only
 * tabs with a lilac capsule behind the active one and the ink scan button seated
 * in the middle. Shown below `md` only (the Sidebar takes over above).
 */
export const TabBar: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (!user) return null;

  const [home, library, workspace, account] = NAV_ITEMS;
  const slot = (item: NavItem) => (
    <Slot key={item.path} item={item} active={isActivePath(pathname, item.path)} onClick={() => navigate(item.path)} />
  );

  return (
    <nav
      aria-label="Main"
      className="fixed left-6 right-6 z-50 flex h-[62px] items-center rounded-full border border-solid border-outline-variant bg-surface-container-lowest px-1.5 shadow-[0_10px_24px_rgba(28,27,25,0.12)] md:hidden"
      style={{ bottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {slot(home)}
      {slot(library)}
      <div className="flex h-full flex-1 items-center justify-center">
        <button
          type="button"
          onClick={() => navigate('/scanner')}
          aria-label="Scan a note"
          className="flex h-[46px] w-[46px] cursor-pointer items-center justify-center rounded-full border border-solid border-[rgba(250,248,244,0.12)] bg-primary text-on-primary shadow-[0_3px_6px_rgba(28,27,25,0.25)] transition-transform duration-150 active:scale-90"
        >
          <Plus size={24} strokeWidth={2.25} />
        </button>
      </div>
      {slot(workspace)}
      {slot(account)}
    </nav>
  );
};
